
import React from "react";
import { IconButton } from "../IconButton";
import { BaseModal, type ModalSize } from "./BaseModal";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  size?: ModalSize;
  /** Escape-Hatch: expliziter CSS max-width (überschreibt `size`). */
  maxWidth?: string;
  /** Tailwind max-w-* Klasse (überschreibt size/maxWidth). */
  maxWidthClass?: string;
  maxHeight?: string;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  hideCloseButton?: boolean;
  bodyPadding?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * „Kann alles"-Modal: {@link BaseModal}-Shell + Header (title/subtitle/Close) +
 * Footer (actions). Für ein rohes, selbst kontrolliertes Modal ohne Chrome die
 * {@link BaseModal} direkt nutzen.
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  actions,
  children,
  size = "md",
  maxWidth,
  maxWidthClass,
  maxHeight,
  closeOnBackdrop = true,
  closeOnEscape = true,
  hideCloseButton = false,
  bodyPadding = true,
  className = "",
  ariaLabel,
}) => {
  const showHead = title !== undefined || subtitle !== undefined || !hideCloseButton;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size={size}
      maxWidth={maxWidth}
      maxWidthClass={maxWidthClass}
      maxHeight={maxHeight}
      closeOnBackdrop={closeOnBackdrop}
      closeOnEscape={closeOnEscape}
      className={className}
      ariaLabel={ariaLabel}
    >
      {showHead && (
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "14px 16px",
            borderBottom: "1px solid var(--ds-border-soft)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
            {title && (
              <h2
                style={{
                  margin: 0,
                  fontSize: "var(--ds-t-lg)",
                  fontWeight: 600,
                  color: "var(--ds-text)",
                }}
              >
                {title}
              </h2>
            )}
            {subtitle && <div style={{ fontSize: "var(--ds-t-sm)", color: "var(--ds-text-3)" }}>{subtitle}</div>}
          </div>
          {!hideCloseButton && <IconButton iconName="close" onClick={onClose} ariaLabel="Schließen" />}
        </header>
      )}
      <div
        style={{
          padding: bodyPadding ? 16 : 0,
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        {children}
      </div>
      {actions && (
        <footer
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 16px",
            borderTop: "1px solid var(--ds-border-soft)",
            background: "var(--ds-bg-sunken)",
            flexShrink: 0,
          }}
        >
          {actions}
        </footer>
      )}
    </BaseModal>
  );
};
